import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleCheck,
  CircleX,
  Mail,
  RotateCw,
  ShieldAlert,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Skeleton,
  StatTile,
  TrendBadge,
  toast,
} from '@/ui';
import { hasPermission } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import {
  DEFAULT_DASHBOARD_RANGE_KEY,
  REPORT_RANGES,
  defaultDashboardRange,
  endOfDayIso,
  formatDay,
  fromDateInputValue,
  rangeStart,
  startOfDayIso,
  toDateInputValue,
  type ReportRangeKey,
} from '@/lib/report-range';
import { sumTotals } from '@/lib/report-volume';
import { totalAmount, type TotalAmount } from '@/lib/money';
import { previousWindow, trendDelta } from '@/lib/trend';

import {
  DASHBOARD_STATUS_KEYS,
  byCurrencyOrder,
  fetchExchangeDashboard,
  sendDashboardReport,
  totalIn,
  type CurrencyTotal,
  type DashboardStatusKey,
} from './api';
import { DASHBOARD_STATUS_LABEL_KEYS, DASHBOARD_STATUS_TONES } from '@/features/reports/labels';
import { dashboardKeys } from './query-keys';
import styles from './Dashboard.module.css';

/**
 * ========================== BURO PANELI (/buro/panel) =======================
 *
 * Mobil `ExchangeDashboard` ekraninin web karsiligi. METRIKLER MOBILLE AYNI
 * (2026-08-30 paritesi), gorsel dil web'e uyarlandi:
 *
 *   hizli aralik cipleri + tarih alanlari   (varsayilan SON 30 GUN)
 *   toplam hacim + "gecen doneme gore" oku
 *   gerceklesen PAYI + tek satirlik yigin bar   (mobilde halka)
 *   uc durum kutusu, her biri kendi trend okuyla
 *   para birimi kirilimi TEK kartta, yigin barlar   (mobilde de oyle)
 *
 * Donem kiyasi SUNUCUDAN GELMIYOR: ayni uc, onceki esit uzunluktaki pencere
 * icin ikinci kez cagriliyor (bkz. lib/trend.ts).
 *
 * -------------------------- BU RAKAMLAR KIMIN ------------------------------
 * Uc `task_offer.offer_user_id = <cagiran>` ile suzuyor: gosterilen ciro
 * ORGANIZASYONUN degil GIRIS YAPMIS KULLANICININ kendi teklif cirosu. Ekranda
 * bu acikca yaziyor — aksi halde bir personel bu sayilari buronun toplami
 * sanip yanlis rapor verir. (Organizasyon geneli: /buro/raporlar.)
 *
 * ------------------------- PARA BIRIMLERI TOPLANMAZ ------------------------
 * 2026-09-23 (karar Q13): hacim teklifin KARSI para biriminde — EUR/USD isinde USD,
 * digerlerinde TRY. Eskiden hepsi tek bir "TRY" toplaminda birlesiyordu. Artik her rakam
 * birim birim cizilir (TRY satiri, USD satiri) ve gerceklesen PAYI tek bir birim uzerinden
 * (TRY varsa TRY) hesaplanir; payi karisik birimlerden hesaplamak anlamsiz bir oran verirdi.
 *
 * ------------------------ HACIM MI, KUR TOPLAMI MI -------------------------
 * BU EKRANDAKI TUTARLAR GERCEK HACIM. Sunucu carpimi kendisi yapiyor
 * (`taskAmount * offerPrice`), bu yuzden TotalAmount olarak isaretlenip
 * <MoneyText total=…> ile ciziliyorlar. Rapor ekranindaki tutarlar OYLE DEGIL
 * — gerekce src/lib/report-volume.ts.
 *
 * ------------------------- YETKI: RAPOR E-POSTASI --------------------------
 * KAYNAKTAN DOGRULANDI (app-service.js `getDashboardData`) ve canli sunucuda
 * sinandi: bu ucun `sendReport` dali HICBIR yetki kodu KONTROL ETMIYOR. Ne
 * `createReport` ne `viewReport`. Kapinin tamami:
 *   1) `getCaller(headers).callerUserId` — yani KIMLIK DOGRULAMA, ve
 *   2) gateway route-policy: `{ userTypes: ['exchange'] }`.
 * Rapor yetkisi OLMAYAN bir personelle cagrildiginda uc 200 + veri donuyor
 * (offers-summary ayni kullaniciyla PERMISSION_DENIED donerken).
 *
 * Bu yuzden butona `createReport` kapisi KONULMADI: sunucunun uygulamadigi bir
 * kural arayuzde uydurulursa, "yetkisi var ama buton yok" ya da tersi bir
 * tutarsizlik olusur ve okuyan kisi kaynagin bu oldugunu sanir. Ekranin
 * tamami zaten rotada `viewReport` ile korunuyor (menus.ts) — sayfayi
 * gorebilen herkes butonu da gorur. Ekrandaki ikinci kapi yalniz bayat bir
 * oturum ozetine karsi: rotaya derin baglantiyla gelinebiliyor.
 * ========================================================================== */

const STATUS_ICONS: Readonly<Record<DashboardStatusKey, LucideIcon>> = Object.freeze({
  accepted: CircleCheck,
  rejected: CircleX,
  warning: TriangleAlert,
});

/**
 * Durum -> kenar serit sinifi. `noUncheckedIndexedAccess` altinda iki indeksli
 * okuma da `| undefined` uretiyor; `exactOptionalPropertyTypes` ise opsiyonel
 * bir prop'a `undefined` gecmeye izin vermiyor. Daralmayi tek yerde yapip
 * HER ZAMAN bir dize donduruyoruz.
 */
const toneClass = (key: DashboardStatusKey): string =>
  styles[DASHBOARD_STATUS_TONES[key] ?? 'neutral'] ?? '';

/**
 * Ayni durum, DOLGU olarak. `toneClass` sol KENARLIK veriyor (kutu seridi);
 * bar dilimleri ve lejant noktalari zemin rengi istiyor. Iki ayri sinif ailesi
 * bilincli: tek sinifa iki farkli ozellik yuklemek, birini degistirince
 * digerini sessizce bozardi.
 */
const fillClass = (key: DashboardStatusKey): string =>
  styles[`fill-${DASHBOARD_STATUS_TONES[key] ?? 'neutral'}`] ?? '';

export function ExchangeDashboardPage() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const snapshot = useSessionStore((state) => state.snapshot);
  const canViewReport = hasPermission(snapshot, 'viewReport');

  // Varsayilan aralik bir KEZ hesaplanir. Her render'da `new Date()` cagirmak
  // sorgu anahtarini milisaniye basina degistirir ve sonsuz istek dongusu acar.
  const [range, setRange] = useState(() => defaultDashboardRange());
  // Hangi hizli aralik secili — `custom` = tarihler elle degistirildi.
  // Cip ile pencere TEK KAYNAKTAN: ikisi ayri yazilsaydi "Son 30 gun" secili
  // gorunurken tarihler baska bir araligi gosterebilirdi (bir kez oldu).
  const [rangeKey, setRangeKey] = useState<ReportRangeKey | 'custom'>(DEFAULT_DASHBOARD_RANGE_KEY);

  const startIso = startOfDayIso(range.start);
  const endIso = endOfDayIso(range.end);

  const query = useQuery({
    queryKey: dashboardKeys.range(startIso, endIso),
    queryFn: () => fetchExchangeDashboard(range),
    enabled: canViewReport,
  });

  /**
   * ONCEKI DONEM — "gecen doneme gore" oklarinin kaynagi (mobil paritesi).
   *
   * Sunucu kiyas yapmiyor; uc yalniz bir aralik aliyor. Bu yuzden ayni ucu
   * secili pencerenin hemen oncesindeki ESIT UZUNLUKTA pencere icin bir kez
   * daha cagiriyoruz (bkz. lib/trend.ts). Ikinci istek BLOKLAMIYOR: ana sorgu
   * kendi basina cizilir, rozetler veri gelince belirir — kiyas bir suslemedir,
   * ana rakami bekletmemeli.
   */
  const previous = useMemo(() => previousWindow(range.start, range.end), [range.start, range.end]);
  const prevStartIso = startOfDayIso(previous.start);
  const prevEndIso = endOfDayIso(previous.end);

  const previousQuery = useQuery({
    queryKey: dashboardKeys.range(prevStartIso, prevEndIso),
    queryFn: () => fetchExchangeDashboard(previous),
    enabled: canViewReport,
  });

  /** Hazir aralik secimi: baslangici jetondan alir, bitis HER ZAMAN bugun. */
  const applyQuickRange = (key: ReportRangeKey): void => {
    const found = REPORT_RANGES.find((item) => item.key === key);
    if (!found) return;
    const now = new Date();
    // 'all' sinirsiz demek; panel ucu tarih ZORUNLU tuttugu icin (api.ts) fiilen
    // "her sey" anlamina gelen erken bir baslangic gonderilir.
    const start = rangeStart(found, now) ?? new Date(2000, 0, 1);
    setRangeKey(key);
    setRange({ start, end: now });
  };

  /**
   * E-posta gonderimi MUTASYON, sorgu degil: yan etkisi var. `sendReport`'u
   * sorgudan gecirmek, React Query'nin her yeniden denemesinde kullaniciya bir
   * e-posta daha yollardi.
   */
  const sendReport = useMutation({
    mutationFn: () => sendDashboardReport(range),
    onSuccess: () => {
      toast({ message: t('dashboard.reportQueued'), tone: 'success' });
    },
    onError: () => {
      toast({ message: t('dashboard.reportFailed'), tone: 'danger' });
    },
  });

  const onDateChange = (which: 'start' | 'end', value: string): void => {
    const parsed = fromDateInputValue(value);
    // Yarim yazilmis bir tarih ("2026-0") gecersizdir; aralik DEGISMEZ.
    // Sifirlansaydi kullanici yazarken sorgu bos araliga duserdi.
    if (!parsed) return;
    // Elle tarih secildi: hazir aralik cipleri artik bu pencereyi TEMSIL ETMIYOR.
    setRangeKey('custom');
    setRange((current) => ({ ...current, [which]: parsed }));
  };

  // Ozet bayat kalabilir (yetki geri alinmis olabilir) ve rotaya derin
  // baglantiyla gelinebiliyor — ekranin kendi kapisi bu yuzden var.
  if (!canViewReport) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('dashboard.noPermission')}
          description={t('dashboard.noPermissionDetail')}
        />
      </Card>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.title}>{t('dashboard.title')}</h1>
          <p className={styles.subtitle}>{t('dashboard.ownFiguresNote')}</p>
        </div>
        <div className={styles.headActions}>
          <Button
            tone="neutral"
            size="sm"
            iconLeft={<RotateCw size={16} aria-hidden="true" />}
            loading={query.isFetching}
            onClick={() => {
              void client.invalidateQueries({ queryKey: dashboardKeys.all });
            }}
          >
            {t('dashboard.refresh')}
          </Button>
          {/* Sunucu bu eylemde AYRI bir yetki aramiyor (bkz. dosya basligi):
              sayfayi gorebilen herkes raporunu isteyebilir. */}
          <Button
            size="sm"
            iconLeft={<Mail size={16} aria-hidden="true" />}
            loading={sendReport.isPending}
            onClick={() => {
              sendReport.mutate();
            }}
          >
            {t('dashboard.sendReport')}
          </Button>
        </div>
      </div>

      <Card padding="md">
        {/* Hizli araliklar tarihlerin USTUNDE: cip pencereyi kurar, tarih
            alanlari onu elle daraltir. Liste Rapor ekraniyla ORTAK
            (lib/report-range.ts) — iki ekran ayni pencereleri sunmali. */}
        {/* `Chip` DEGIL gercek <button>: Chip bir <span> ve tiklanabilir bir
            filtre olarak ne odaklanabilir ne klavyeyle calisir. `aria-pressed`
            secili olani soyler; SegmentedTabs de kullanilmadi cunku tarihler
            elle degistirildiginde HICBIRI secili olmamali ('custom') ve bir
            tablist'te "hicbiri secili degil" hali yoktur. */}
        <div className={styles.chipRow} role="group" aria-label={t('dashboard.rangeQuick')}>
          {REPORT_RANGES.map((item) => (
            <button
              key={item.key}
              type="button"
              className={[styles.rangeChip, rangeKey === item.key ? styles.rangeChipOn : undefined]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={rangeKey === item.key}
              onClick={() => {
                applyQuickRange(item.key);
              }}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        <div className={styles.rangeRow}>
          <FormField label={t('dashboard.dateFrom')} className={styles.rangeField}>
            <Input
              type="date"
              value={toDateInputValue(range.start)}
              max={toDateInputValue(range.end)}
              onChange={(event) => {
                onDateChange('start', event.target.value);
              }}
            />
          </FormField>
          <FormField label={t('dashboard.dateTo')} className={styles.rangeField}>
            <Input
              type="date"
              value={toDateInputValue(range.end)}
              min={toDateInputValue(range.start)}
              onChange={(event) => {
                onDateChange('end', event.target.value);
              }}
            />
          </FormField>
        </div>
        <p className={styles.muted}>
          {t('dashboard.rangeSummary', {
            from: formatDay(range.start),
            to: formatDay(range.end),
          })}
        </p>
        {/* "Kendi adresine gonderilir" — sunucu aliciyi cagiranin kaydindan
            cozuyor, istekten DEGIL. Kullanici baska bir adres bekliyorsa
            bunu ONCEDEN bilmeli. */}
        <p className={styles.notice}>
          {t('dashboard.reportRecipientNote', {
            email: snapshot?.user.userEmail ?? t('dashboard.reportRecipientUnknown'),
          })}
        </p>
      </Card>

      <DashboardBody query={query} previousQuery={previousQuery} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Govde — yukleniyor / hata / veri                                            */
/* -------------------------------------------------------------------------- */

type DashboardQuery = ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchExchangeDashboard>>>>;

/** Artis IYI mi? Kazanilan hacimde evet; kaybedilen ve uyarida hayir. */
const GOOD_DIRECTION: Readonly<Record<DashboardStatusKey, 'up' | 'down'>> = Object.freeze({
  accepted: 'up',
  rejected: 'down',
  warning: 'down',
});

function DashboardBody({
  query,
  previousQuery,
}: {
  query: DashboardQuery;
  previousQuery: DashboardQuery;
}) {
  const { t } = useTranslation();

  const data = query.data;
  const prev = previousQuery.data;

  /**
   * ISLEM TIPI KIRILIMI TEK TABLODA (mobil paritesi).
   *
   * Satirlar islem tipine gore birlesiyor, her satir uc parcali yigin bar. Bir satirin
   * parcalari AYNI birimde (o tipin karsi para birimi) oldugu icin satir ici toplam anlamli;
   * satirlar ARASI siralama once birime (TRY once), sonra buyukluge gore.
   */
  const currencyRows = useMemo(() => {
    if (!data) return [];
    const zero = (): Record<DashboardStatusKey, TotalAmount> => ({
      accepted: totalAmount(0),
      rejected: totalAmount(0),
      warning: totalAmount(0),
    });
    const totals = new Map<
      string,
      { counterCurrency: string; parts: Record<DashboardStatusKey, TotalAmount> }
    >();
    for (const key of DASHBOARD_STATUS_KEYS) {
      for (const item of data.buckets[key].currencies) {
        const row = totals.get(item.code) ?? { counterCurrency: item.counterCurrency, parts: zero() };
        row.parts[key] = item.total;
        totals.set(item.code, row);
      }
    }
    return [...totals.entries()]
      .map(([code, { counterCurrency, parts }]) => ({
        code,
        counterCurrency,
        parts,
        // `sumTotals` markali tipi koruyor: duz toplama TotalAmount'u `number`a
        // dusurur ve MoneyText `total=` propunu kabul etmez (tam da bu yakalandi).
        total: sumTotals(DASHBOARD_STATUS_KEYS.map((k) => parts[k])),
      }))
      .filter((row) => row.total > 0)
      .sort(
        (a, b) => byCurrencyOrder(a.counterCurrency, b.counterCurrency) || b.total - a.total,
      );
  }, [data]);

  if (query.isPending) {
    return (
      <div className={styles.loading}>
        <Skeleton shape="block" height="96px" />
        <Skeleton shape="block" height="120px" />
        <Skeleton shape="block" height="160px" />
      </div>
    );
  }

  /**
   * HATA SESSIZCE "0,00 TL" OLMAZ. Yanit sekli beklenmedikse api.ts
   * ApiShapeError firlatiyor ve buraya dusuyor; sifir gosteren bir panel
   * "yukleyemedim" diyen bir panelden daha kotu.
   */
  if (query.isError || !data) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<TriangleAlert size={28} aria-hidden="true" />}
          title={t('dashboard.loadFailed')}
          description={t('dashboard.loadFailedDetail')}
          action={
            <Button
              tone="neutral"
              size="sm"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const nonZeroTotals = data.totals.filter((item) => item.total > 0);
  const isEmpty = nonZeroTotals.length === 0;
  // Gerceklesen PAYI TEK BIR BIRIMDE: TRY varsa TRY, yoksa ilk birim. Karisik birimlerden
  // oran hesaplamak (USD'yi TRY'ye eklemek) anlamsiz bir yuzde verirdi.
  const shareCurrency =
    nonZeroTotals.find((item) => item.currency === 'TRY')?.currency ??
    nonZeroTotals[0]?.currency ??
    'TRY';
  const shareTotal = totalIn(data.totals, shareCurrency);
  const acceptedShare = shareTotal
    ? Math.round((totalIn(data.buckets.accepted.sums, shareCurrency) / shareTotal) * 100)
    : null;
  // Toplam kutusunda gosterilecek birimler: veri yoksa bile TRY satiri (0) cizilir.
  const leaderTotals: readonly CurrencyTotal[] =
    nonZeroTotals.length > 0 ? nonZeroTotals : [{ currency: 'TRY', total: totalAmount(0) }];

  return (
    <>
      {/* Vitrin lideri: toplam hacim kutusuz, mega foil metin (bkz. .leader).
          Birim basina bir satir; yanindaki ok "gecen doneme gore" AYNI birimde. */}
      <div className={styles.leader}>
        <p className={styles.tileLabel}>{t('dashboard.totalVolume')}</p>
        {leaderTotals.map((item) => {
          const prevTotal = prev ? totalIn(prev.totals, item.currency) : 0;
          return (
            <div key={item.currency}>
              <p className={styles.leaderValue}>
                <MoneyText total={item.total} currency={item.currency} size="display" />
                <TrendBadge delta={trendDelta(item.total, prevTotal)} />
              </p>
              {prev && prevTotal > 0 ? (
                <p className={styles.tileLabel}>
                  {t('dashboard.prevPeriodValue', { value: '' })}
                  <MoneyText
                    total={totalIn(prev.totals, item.currency)}
                    currency={item.currency}
                    size="caption"
                  />
                </p>
              ) : null}
            </div>
          );
        })}
        {leaderTotals.length > 1 ? (
          <p className={styles.tileLabel}>{t('dashboard.currencySeparateNote')}</p>
        ) : null}
      </div>

      {/* Durumlarin toplam hacimdeki payi — mobildeki halkanin web karsiligi.
          Tek satirlik yigin bar, TEK BIRIMDE (shareCurrency). */}
      {isEmpty ? null : (
        <Card padding="md">
          <div className={styles.shareHead}>
            <span className={styles.tileLabel}>
              {t('dashboard.realizedShareIn', { currency: shareCurrency })}
            </span>
            <strong className={styles.shareValue}>
              {acceptedShare === null ? '—' : `%${acceptedShare}`}
            </strong>
          </div>
          <div
            className={styles.shareBar}
            role="img"
            aria-label={t('dashboard.shareBarLabel')}
          >
            {DASHBOARD_STATUS_KEYS.map((key) => {
              const value = totalIn(data.buckets[key].sums, shareCurrency);
              if (value <= 0) return null;
              return (
                <span
                  key={key}
                  className={[styles.shareSegment, fillClass(key)].join(' ')}
                  style={{ flexGrow: value }}
                />
              );
            })}
          </div>
          {/* Bu barin LEJANTI YOK: hemen altindaki durum kutulari ayni etiketleri
              ayni renk seridiyle zaten tasiyor. Ikinci bir lejant, ayni uc
              kelimeyi ekranda uc kez yazdirmaktan baska bir sey yapmiyordu. */}
        </Card>
      )}

      <div className={styles.tiles}>
        {DASHBOARD_STATUS_KEYS.map((key) => {
          const Icon = STATUS_ICONS[key];
          const bucket = data.buckets[key];
          const lines = bucket.sums.filter((item) => item.total > 0);
          const shown: readonly CurrencyTotal[] =
            lines.length > 0 ? lines : [{ currency: 'TRY', total: totalAmount(0) }];
          return (
            <StatTile
              key={key}
              className={toneClass(key)}
              icon={<Icon size={16} aria-hidden="true" />}
              label={t(DASHBOARD_STATUS_LABEL_KEYS[key] ?? key)}
              value={
                <span className={styles.valueStack}>
                  {shown.map((item) => (
                    <span key={item.currency} className={styles.tileValueRow}>
                      <MoneyText total={item.total} currency={item.currency} size="h2" />
                      <TrendBadge
                        delta={trendDelta(
                          item.total,
                          prev ? totalIn(prev.buckets[key].sums, item.currency) : 0,
                        )}
                        goodDirection={GOOD_DIRECTION[key]}
                      />
                    </span>
                  ))}
                </span>
              }
              // Degisken adi `n` — ceviri '{{n}} para birimi' yaziyor. `count` ile
              // cagrildiginda i18next onu COGUL SAYACI sanip {{n}}'i cozemiyordu
              // ve ekranda ham sablon ("{{n}} para birimi") gorunuyordu.
              hint={t('dashboard.currencyCount', { n: bucket.currencies.length })}
            />
          );
        })}
      </div>

      {isEmpty || currencyRows.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Wallet size={28} aria-hidden="true" />}
            title={t('dashboard.noData')}
            description={t('dashboard.noDataDetail')}
          />
        </Card>
      ) : (
        <Card title={t('dashboard.byCurrencyTitle')} padding="md">
          <p className={styles.tileLabel}>{t('dashboard.byCurrencyHint')}</p>
          <ul className={styles.stackList}>
            {currencyRows.map((row) => (
              <li key={row.code} className={styles.stackRow}>
                <div className={styles.stackHead}>
                  <span className={styles.stackCode}>{row.code}</span>
                  {/* Satirin birimi O TIPIN karsi para birimi: EUR/USD'de USD. */}
                  <MoneyText total={row.total} currency={row.counterCurrency} size="caption" />
                </div>
                <div className={styles.stackBar}>
                  {DASHBOARD_STATUS_KEYS.map((key) => {
                    const value = row.parts[key];
                    if (value <= 0) return null;
                    const share = Math.round((value / row.total) * 100);
                    return (
                      <span
                        key={key}
                        className={[styles.stackSegment, fillClass(key)].join(' ')}
                        style={{ flexGrow: value }}
                        title={`${t(DASHBOARD_STATUS_LABEL_KEYS[key] ?? key)} %${share}`}
                      >
                        {/* Dar dilimde yuzde sigmaz; etiket yalniz payi yeterli
                            dilime yazilir, dar dilim rengiyle konusur. */}
                        {share >= 14 ? <span className={styles.stackPct}>%{share}</span> : null}
                      </span>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
          <div className={styles.shareLegend}>
            {DASHBOARD_STATUS_KEYS.map((key) => (
              <span key={key} className={styles.legendItem}>
                <span className={[styles.legendDot, fillClass(key)].join(' ')} aria-hidden="true" />
                {t(DASHBOARD_STATUS_LABEL_KEYS[key] ?? key)}
              </span>
            ))}
          </div>
          {/* Ozet "ne oldu"yu, rapor "dokumunu" verir — mobilde de ozetin
              devami olarak buradan aciliyor. */}
          <p className={styles.reportsLink}>
            <Link to="/buro/raporlar">{t('dashboard.seeReports')}</Link>
          </p>
        </Card>
      )}
    </>
  );
}
